// Deep Signal → Competitive.
//
// Two blocks: the Competitor Recommendation Funnel, and Competitive
// Intelligence's Attribute rankings.
//
// Attribute rankings MOVED here from Brand Monitoring, where it was block 2 of
// competitive-intelligence-section. It replaced Per-Model Competitive Presence,
// a grid of one mention-rate card per model. That per-model capability was not
// dropped: it is now a filter on the funnel table below, reading the same
// competitor per_model payload the cards read.
//
// This tab is the DESTINATION for the insight rail, which lives on Brand
// Monitoring (the glanceable teaser layer). The rail is not rendered here — it
// links in, preselecting an attribute via the nav target, which arrives on the
// context as `initialAttribute` and drives the Attribute rankings tab below.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDeepSignal } from '../context';
import { buildCompetitiveFromApi, competitiveInstrumentNote } from '../builders';
import { SectionLabel, SectionTitle, DSLoadingRow, DSEmptyState } from '../components/section-primitives';
import { AttributeRankingsSection } from '@/app/components/antenna/competitive-intelligence/attribute-rankings-section';
import {
  createActionFromGap,
  type GapDescriptor,
} from '@/app/services/fix-actions/create-action-from-gap';
import type { CompetitorFunnelRow, FunnelStageKey } from '../types';

const STAGE_LABELS: Record<FunnelStageKey, string> = {
  mentionRate: 'Mention Rate',
  top5Rate: 'Top-5 Rate',
  primaryRecRate: 'Primary Rec',
};

/** "All models" sentinel for the funnel table's model filter. */
const ALL = '__all';

export function CompetitiveSection() {
  const { apiData, loading, onNavigate, initialAttribute } = useDeepSignal();
  const [model, setModel] = useState<string>(ALL);

  // External attribute selection. Kept after the rail moved to Brand
  // Monitoring: it is now driven by the nav target that page sends. The nonce
  // lets the same attribute be re-applied on a repeat arrival.
  const [selected, setSelected] = useState<{ attribute: string; nonce: number } | null>(null);
  const attrRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!initialAttribute) return;
    setSelected((prev) => ({ attribute: initialAttribute, nonce: (prev?.nonce ?? 0) + 1 }));
    // Deferred: the section has to mount and lay out before it can be scrolled
    // to, and arriving from another page means this runs on that first paint.
    const t = setTimeout(
      () => attrRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      350,
    );
    return () => clearTimeout(t);
  }, [initialAttribute]);

  const built = useMemo(
    () => (apiData ? buildCompetitiveFromApi(apiData) : null),
    [apiData],
  );

  // Writes a real Action Log row from an attribute gap. Same ingest path the
  // Brand Monitoring copy used; the brand and run come from this page's data.
  const handleCreateActionFromGap = useMemo(() => {
    if (!apiData) return undefined;
    const brand = apiData.customer.brand_name;
    const brandId = String(apiData.run?.id ?? '');
    const modelsUsed = Object.keys(apiData.model_breakdown ?? {});
    return (gap: GapDescriptor) => createActionFromGap(gap, { brand, brandId, modelsUsed });
  }, [apiData]);


  if (loading) return (
    <div style={{ padding: '32px 64px' }}>
      {[1,2,3,4,5].map(i => <DSLoadingRow key={i} />)}
    </div>
  );
  if (!apiData || !built) return <DSEmptyState message="No competitive data available." />;

  const { funnelRows, models, visibleStages, instruments } = built;

  // No measured column at all: an honest empty state beats a table of dashes.
  if (visibleStages.length === 0) {
    return <DSEmptyState message="Competitive measurement is not available for this run yet." />;
  }

  const instrumentNote = competitiveInstrumentNote(instruments);

  // The payload carries per-model rates for MENTION only (per_model is a
  // model-key -> mention_rate map on each competitor). So filtering to one
  // model narrows the table to the column that model actually has, rather than
  // keeping three columns and filling two of them with dashes.
  const filtered = model !== ALL;
  const stages: FunnelStageKey[] = filtered
    ? visibleStages.filter(s => s === 'mentionRate')
    : visibleStages;

  const rows: CompetitorFunnelRow[] = filtered
    ? [...funnelRows]
        .map(r => ({ ...r, mentionRate: r.perModel?.[model] ?? null }))
        .sort((a, b) => (b.mentionRate ?? -1) - (a.mentionRate ?? -1))
    : funnelRows;

  const cellValue = (row: CompetitorFunnelRow, key: FunnelStageKey) => {
    const v = row[key];
    return v == null ? '—' : `${v.toFixed(1)}%`;
  };

  const cellColor = (row: CompetitorFunnelRow, key: FunnelStageKey) => {
    if (key === 'primaryRecRate' && row.focal && row.primaryRecRate != null) {
      return row.primaryRecRate >= 25 ? '#00c9c4' : row.primaryRecRate >= 15 ? '#28a9d2' : '#ff6d66';
    }
    return row.focal ? '#00c9c4' : 'var(--foreground)';
  };

  return (
    <div style={{ padding: '28px 0 40px' }}>

      {/* ── Full Recommendation Funnel ───────────────────────────────────── */}
      <div style={{ padding: '0 32px 28px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
          <div style={{ minWidth: 0 }}>
            <SectionLabel>Competitor Recommendation Funnel</SectionLabel>
            <SectionTitle size="xl">Where each competitor stands at every stage of the funnel.</SectionTitle>
          </div>
          {/* Model filter. Carries over what the Per-Model Competitive Presence
              cards showed — each model's read of the competitive field — as a
              control on this table instead of a second grid below it. */}
          {models.length > 0 && (
            <div style={{ display: 'inline-flex', gap: '2px', padding: '3px', backgroundColor: 'var(--muted)', borderRadius: '9px', flexShrink: 0 }}>
              {[{ key: ALL, label: 'All models' }, ...models].map(m => {
                const on = model === m.key;
                return (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => setModel(m.key)}
                    style={{
                      border: 'none', cursor: 'pointer', borderRadius: '7px', padding: '5px 11px',
                      fontSize: '12px', fontWeight: on ? 600 : 500, fontFamily: 'inherit',
                      backgroundColor: on ? 'var(--card)' : 'transparent',
                      color: on ? 'var(--foreground)' : 'var(--muted-foreground)',
                      boxShadow: on ? '0 1px 2px rgba(13,13,15,.12)' : 'none',
                    }}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)', lineHeight: '1.5', marginBottom: instrumentNote ? '8px' : '20px' }}>
          Mention rate only tells part of the story. This shows how each competitor performs at each stage of the funnel, from being mentioned to making the shortlist to becoming the primary recommendation. Your own funnel is on the main funnel view.
        </p>
        {instrumentNote && (
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)', lineHeight: '1.5', marginBottom: filtered ? '8px' : '20px' }}>
            {instrumentNote}
          </p>
        )}
        {/* Said plainly rather than shown as two columns of dashes: the run
            stores competitor rates per model for mention only. */}
        {filtered && (
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)', lineHeight: '1.5', marginBottom: '20px' }}>
            Per-model competitor rates are measured for mention only, so top-5 and primary
            recommendation are not broken out by model. Switch to All models to see the full funnel.
          </p>
        )}

        {/* The table is full width now: the insight callout that used to sit
            beside it is the rail below. */}
        <div style={{ display: 'flex', gap: '24px', alignItems: 'stretch' }}>
          <div style={{ flex: '1 1 auto', minWidth: 0 }}>
            {/* Header */}
            <div style={{ display: 'flex', padding: '0 16px 10px', borderBottom: '1px solid var(--border)' }}>
              <span style={{ flex: 2, fontSize: 'var(--text-xs)', fontWeight: 600, letterSpacing: '0.2px', color: 'var(--muted-foreground)' }}>Brand</span>
              {stages.map(key => (
                <span key={key} style={{ flex: 1, textAlign: 'right', fontSize: 'var(--text-xs)', fontWeight: 600, letterSpacing: '0.2px', color: 'var(--muted-foreground)' }}>
                  {STAGE_LABELS[key]}
                </span>
              ))}
            </div>
            {rows.map((row, i) => (
              <div key={row.brand} style={{
                display: 'flex', alignItems: 'center', padding: '14px 16px',
                borderLeft: row.focal ? '3px solid #00c9c4' : '3px solid transparent',
                borderBottom: i < rows.length - 1 ? '1px solid var(--border)' : 'none',
                backgroundColor: row.focal ? '#f0fefe' : 'transparent',
              }}>
                <span style={{ flex: 2, fontSize: 'var(--text-sm)', fontWeight: row.focal ? 600 : 400, color: row.focal ? '#00c9c4' : 'var(--foreground)' }}>
                  {row.brand}
                </span>
                {stages.map(key => (
                  <span key={key} style={{
                    flex: 1, textAlign: 'right', fontSize: 'var(--text-sm)',
                    fontWeight: row.focal ? 600 : 400,
                    color: cellColor(row, key),
                  }}>
                    {cellValue(row, key)}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* ── Attribute rankings (moved here from Brand Monitoring) ───────── */}
      <div style={{ padding: '28px 32px 0' }} ref={attrRef}>
        <div style={{ height: '1px', backgroundColor: 'var(--border)', marginBottom: '24px' }} />
        <AttributeRankingsSection
          brandName={apiData.customer.brand_name}
          onNavigate={onNavigate}
          onCreateAction={handleCreateActionFromGap}
          selectedAttribute={selected?.attribute ?? null}
          selectionNonce={selected?.nonce}
        />
      </div>
    </div>
  );
}
